import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Tooltip,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Preview,
  Image,
  VideoLibrary,
  OpenInNew,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
} from '@mui/icons-material';
import { getStaticFileUrl } from '../../utils/config';

interface MediaPreviewProps {
  src: string;
  alt?: string;
  title?: string;
  type?: 'image' | 'video' | 'auto';
  width?: number | string;
  height?: number | string;
  showPreviewButton?: boolean;
  showTypeChip?: boolean;
}

export const MediaPreview: React.FC<MediaPreviewProps> = ({
  src,
  alt = '미디어',
  title,
  type = 'auto',
  width = 200,
  height = 150,
  showPreviewButton = true,
  showTypeChip = true,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 서버 URL이 포함된 완전한 URL 생성
  const fullSrc = getStaticFileUrl(src);

  // 미디어 타입 자동 감지
  const getMediaType = () => {
    if (type !== 'auto') return type;
    
    const extension = src.split('.').pop()?.toLowerCase();
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    
    if (videoExtensions.includes(extension || '')) return 'video';
    if (imageExtensions.includes(extension || '')) return 'image';
    
    // URL 패턴으로 판단 (더 구체적인 패턴 추가)
    if (src.includes('video/') || 
        src.includes('.mp4') || 
        src.includes('디지털콘텐츠') ||
        src.includes('방송프로그램') ||
        src.includes('홍보영상') ||
        src.includes('오프라인행사') ||
        src.includes('유튜브영상')) return 'video';
    if (src.includes('image/') || 
        src.includes('img/') || 
        src.includes('portfolio/') ||
        src.includes('about/') ||
        src.includes('contact/') ||
        src.includes('home/')) return 'image';
    
    return 'image'; // 기본값
  };

  const mediaType = getMediaType();
  const isVideo = mediaType === 'video';

  // 비디오 이벤트 핸들러
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPreview(true);
  };

  const handleExternalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(fullSrc, '_blank');
  };

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  const toggleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      if (!document.fullscreenElement) {
        video.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  return (
    <>
      <Card
        sx={{
          width,
          height,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover .preview-overlay': {
            opacity: 1,
          },
          '&:hover .video-controls': {
            opacity: 1,
          },
        }}
        onClick={handlePreviewClick}
      >
        {isVideo ? (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <video
              ref={videoRef}
              src={fullSrc}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              muted={isMuted}
              loop
              playsInline
            />
            
            {/* 비디오 컨트롤 오버레이 */}
            <Box
              className="video-controls"
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                padding: 1,
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              <Tooltip title={isPlaying ? '일시정지' : '재생'}>
                <IconButton
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: 'black',
                    '&:hover': { backgroundColor: 'white' }
                  }}
                  onClick={togglePlayPause}
                >
                  {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                </IconButton>
              </Tooltip>
              
              <Tooltip title={isMuted ? '음소거 해제' : '음소거'}>
                <IconButton
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: 'black',
                    '&:hover': { backgroundColor: 'white' }
                  }}
                  onClick={toggleMute}
                >
                  {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
                </IconButton>
              </Tooltip>
              
              <Tooltip title="전체화면">
                <IconButton
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: 'black',
                    '&:hover': { backgroundColor: 'white' }
                  }}
                  onClick={toggleFullscreen}
                >
                  <Fullscreen fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <img
            src={fullSrc}
            alt={alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        
        {/* 미리보기 오버레이 */}
        <Box
          className="preview-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {showPreviewButton && (
              <Tooltip title="미리보기">
                <IconButton
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { backgroundColor: 'white' }
                  }}
                  onClick={handlePreviewClick}
                >
                  <Preview fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="새 탭에서 열기">
              <IconButton
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': { backgroundColor: 'white' }
                }}
                onClick={handleExternalClick}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 타입 칩 */}
        {showTypeChip && (
          <Chip
            icon={isVideo ? <VideoLibrary fontSize="small" /> : <Image fontSize="small" />}
            label={isVideo ? '비디오' : '이미지'}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontSize: '0.75rem',
            }}
          />
        )}

        {/* 제목 */}
        {title && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: 1,
            }}
          >
            <Typography variant="caption" noWrap>
              {title}
            </Typography>
          </Box>
        )}
      </Card>

      {/* 미리보기 다이얼로그 */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              미디어 미리보기
            </Typography>
            <Chip
              icon={isVideo ? <VideoLibrary /> : <Image />}
              label={isVideo ? '비디오' : '이미지'}
              color="primary"
              variant="outlined"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            {isVideo ? (
              <Box sx={{ position: 'relative' }}>
                <video
                  src={fullSrc}
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    borderRadius: '8px',
                  }}
                  autoPlay
                />
              </Box>
            ) : (
              <img
                src={fullSrc}
                alt={alt}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
            )}
            {title && (
              <Typography variant="h6" sx={{ mt: 2 }}>
                {title}
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {fullSrc}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open(fullSrc, '_blank')}>
            새 탭에서 열기
          </Button>
          <Button onClick={() => setShowPreview(false)}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 